import { z } from "zod"
import type { ZodType } from "zod"

import {
  EByteDanceImageGenerationModelType,
  ELipsyncModel,
  FluxImageGenerationModelType,
  GoogleImageGenerationModelType,
  GoogleVideoGenerationModelType,
  KlingAIVideoGenerationModelType,
  LumaVideoGenerationModelType,
  MinimaxImageGenerationModelType,
  MinimaxVideoGenerationModelType,
  NvidiaImageGenerationModelType,
  OpenAIImageGenerationModelType,
  ReplicateVideoGenerationModelType,
  SunoAudioGenerationModelType,
  WavespeedVideoGenerationModelType,
} from "../image/enums"
import type { IAllModelTypes, ListedLLMModelType } from "../llms/enums"
import { getTokenCount } from "../llms/tokenCount"
import { getLlmRate, lipsyncEngineMeta, llmEngineMeta, musicEngineMeta, ttsEngineMeta } from "./modalityPricing"
import { EPricingUnit, replicateImageModelPricing, replicateModelPricing } from "./replicate/pricing"

// Define the model types enum (if not already present)
export enum ModelType {
  LLM = "LLM",
  IMAGE = "IMAGE",
  VIDEO = "VIDEO",
  TTS = "TTS",
}

// Define the specific rate keys
type RateKey = "textInput" | "textOutput" | "imageInput" | "imageOutput" | "cachedTextInput" | "secondsOutput"
type RateUsage = Partial<Record<RateKey, number>> // Type alias for meter result

// Exporting for use in other packages
export type { RateKey, RateUsage }

// Parameter Schemas & Types
// 1. LLM Text Params
export interface LlmParams {
  inputTokens: number
  outputTokens: number
  cachedTextInput?: number // Optional cached input
}

// 2. OpenAI GPT-Image Params
export interface GptImageParams {
  prompt: string
  pxW: number
  pxH: number
  quality: "standard" | "HD"
}
const gptImageSchema = z.object({
  prompt: z.string().min(1), // Allow empty prompt? Adjusted min length
  pxW: z.number().int().positive(),
  pxH: z.number().int().positive(),
  quality: z.enum(["standard", "HD"]), // Use standard instead of SD
})

// 3. Simple Quantity Params (e.g., DALL-E 3, Flux)
export interface QuantityParams {
  quantity: number
}
const quantitySchema = z.object({
  quantity: z.number().int().positive(),
})

// 4. Simple Seconds Params (e.g., Kling)
export interface SecondsParams {
  seconds: number
}
const secondsSchema = z.object({
  seconds: z.number().positive(),
})

// 5. TTS Params
export interface TtsParams {
  characterCount: number
}

// Updated EngineMeta with Generics, Zod Schema, and modelType
export interface EngineMeta<P extends object = any> {
  displayName: string
  unit: "token" | "seconds" | "quantity" | "character"
  ratesUSD: Partial<Record<RateKey, number>>
  paramSchema: ZodType<P> // NEW: Zod schema for runtime validation
  meter: (params: P) => RateUsage // Typed meter function
  modelType: ModelType // Re-added model type
  /**
   * Output rate per second broken out by resolution, for models the provider prices per tier.
   *
   * `ratesUSD.secondsOutput` stays the model's BASE (lowest-tier) rate so existing callers are
   * unaffected; callers that know which tier they submitted read the exact rate from here instead.
   * Without this the registry can only express one rate for a model whose real price doubles
   * between 480p and 720p — which is how UGC ended up billing Aurora and Fabric at their 480p
   * rate while always submitting 720p.
   */
  resolutionRatesUSD?: Partial<Record<"480p" | "720p" | "1080p", number>>
}

// Define a comprehensive type that includes all model types
type AllSupportedModelTypes =
  | IAllModelTypes
  | OpenAIImageGenerationModelType
  | FluxImageGenerationModelType
  | GoogleImageGenerationModelType
  | MinimaxImageGenerationModelType
  | NvidiaImageGenerationModelType
  | EByteDanceImageGenerationModelType
  | KlingAIVideoGenerationModelType
  | LumaVideoGenerationModelType
  | ReplicateVideoGenerationModelType
  | WavespeedVideoGenerationModelType
  | MinimaxVideoGenerationModelType
  | GoogleVideoGenerationModelType

// Define a type for MODEL_METADATA to satisfy TypeScript
// We avoid inferring parameters directly in the type definition to prevent circular issues.
// The inference happens correctly when using the object (e.g., in quoteAllChannels).
type ModelMetadataMap = Record<ListedLLMModelType, EngineMeta> &
  Partial<Record<Exclude<AllSupportedModelTypes, ListedLLMModelType>, EngineMeta>>

/**
 * Image/video models whose METERING view is derived from the commercial registry instead of being
 * restated here.
 *
 * Only models whose commercial `rate` is a plain number qualify. The rest keep a literal entry
 * below: their commercial rate is a FUNCTION of submitted params (resolution, duration, aspect
 * ratio), and collapsing that to the single scalar `ratesUSD` wants would mean picking
 * representative params — a choice, not a derivation, and one that would silently misprice every
 * other parameter combination.
 *
 * `per_image` and `per_request` both meter as `imageOutput` (the key has always doubled as the
 * generic "per output" unit); `per_second` meters as `secondsOutput`.
 */
const COMMERCIAL_METERED_MODEL_IDS = [
  FluxImageGenerationModelType.FLUX_PRO,
  FluxImageGenerationModelType.FLUX_SCHNELL,
  FluxImageGenerationModelType.FLUX_DEV,
  GoogleImageGenerationModelType.IMAGEN_4,
  GoogleImageGenerationModelType.IMAGEN_4_FAST,
  GoogleImageGenerationModelType.IMAGEN_4_ULTRA,
  GoogleImageGenerationModelType.GEMINI_2_5_FLASH_IMAGE,
  NvidiaImageGenerationModelType.SANA,
  MinimaxImageGenerationModelType.MINIMAX_IMAGE_01,
  OpenAIImageGenerationModelType.GPT_IMAGE_1_MINI,
  EByteDanceImageGenerationModelType.SEEDREAM_4_5,
  MinimaxVideoGenerationModelType.VIDEO_01,
  GoogleVideoGenerationModelType.VEO_3,
  KlingAIVideoGenerationModelType.KLING_STANDARD_1_6,
  KlingAIVideoGenerationModelType.KLING_PRO_1_6,
  KlingAIVideoGenerationModelType.KLING_2_5_TURBO_PRO,
  LumaVideoGenerationModelType.RAY_3_2,
] as const

const commercialEngineMeta: Record<string, EngineMeta> = Object.fromEntries(
  COMMERCIAL_METERED_MODEL_IDS.map((modelId) => {
    const entry =
      (replicateImageModelPricing as Record<string, { rate: unknown; unit: string } | undefined>)[modelId] ??
      (replicateModelPricing as Record<string, { rate: unknown; unit: string } | undefined>)[modelId]
    if (!entry || typeof entry.rate !== "number") {
      throw new Error(`[pricing] ${modelId} is listed as commercially metered but has no scalar rate`)
    }
    const perSecond = entry.unit === EPricingUnit.PER_SECOND
    const meta: EngineMeta = perSecond
      ? {
          displayName: modelId,
          unit: "seconds",
          ratesUSD: { secondsOutput: entry.rate },
          paramSchema: secondsSchema,
          meter: (params: SecondsParams): RateUsage => ({ secondsOutput: params.seconds }),
          modelType: ModelType.VIDEO,
        }
      : {
          displayName: modelId,
          unit: "quantity",
          ratesUSD: { imageOutput: entry.rate },
          paramSchema: quantitySchema,
          meter: (params: QuantityParams): RateUsage => ({ imageOutput: params.quantity }),
          modelType: replicateImageModelPricing[modelId as keyof typeof replicateImageModelPricing]
            ? ModelType.IMAGE
            : ModelType.VIDEO,
        }
    return [modelId, meta]
  }),
)

// MODEL_METADATA with modelType added back
// Cast the object to the defined type
export const MODEL_METADATA: ModelMetadataMap = {
  /**********
   * IMAGE + VIDEO with a scalar commercial rate — derived, not restated. See
   * COMMERCIAL_METERED_MODEL_IDS above for why the param-dependent ones stay literal.
   *********/
  ...commercialEngineMeta,

  /**********
   * LLM + WEB SEARCH + TTS
   *
   * No longer literal here. These are derived views over the single rate tables in
   * `modalityPricing.ts`, which also produce the `BaseModelPricing` entries the unified resolver
   * and the pricing tooling read. Spreading them keeps every existing consumer of
   * MODEL_METADATA (quoteAllChannels, the admin UI) working unchanged while the DATA lives in
   * exactly one place — change a rate there and both shapes move together.
   *********/
  ...(llmEngineMeta as Record<string, EngineMeta>),
  ...(ttsEngineMeta as Record<string, EngineMeta>),

  /**********
   * IMAGE MODELS
   *********/
  [OpenAIImageGenerationModelType.DALLE_3]: {
    displayName: "DALL-E 3 HD",
    unit: "quantity",
    ratesUSD: { imageOutput: 0.08 },
    paramSchema: quantitySchema,
    meter: (params: QuantityParams): RateUsage => ({ imageOutput: params.quantity }),
    modelType: ModelType.IMAGE,
  },
  // GPT Image 1 is an IMAGE model that bills in token-like units across three rate keys, with a
  // bespoke meter (prompt tokens + fixed image-input + megapixel-scaled output). It does not fit
  // the flat LLM/TTS rate tables, so it stays literal here. Declared AFTER the spreads above so
  // this definition wins over the LLM view's entry, which exists only so
  // `calculateTokenUsageAndCost` can still price a GPT-Image prompt.
  [OpenAIImageGenerationModelType.GPT_IMAGE]: {
    displayName: "GPT Image 1",
    unit: "token",
    ratesUSD: {
      // Prompt tokens bill at the LLM input rate, read from the one rate table rather than
      // restated here — two independent copies of the same number is the drift this file's
      // migration exists to remove.
      textInput: getLlmRate(OpenAIImageGenerationModelType.GPT_IMAGE)?.input ?? 0,
      imageInput: 10.0 / 1_000_000,
      imageOutput: 40.0 / 1_000_000,
    },
    paramSchema: gptImageSchema,
    meter: (params: GptImageParams): RateUsage => {
      const { prompt, pxW, pxH, quality } = params
      const ti = getTokenCount(prompt) // textInput units
      const ii = 350 // imageInput units (fixed based on original logic)
      // MEGApixels — the previous `pxW * pxH` was raw pixels, so a 1024x1024 image metered
      // 1,048,576 "megapixels" and quoted $20,971 instead of ~$0.02. Only the admin price preview
      // reads this path, so nothing was ever charged that, but the number it showed was nonsense.
      // At 1 MP the output lands on ~524 tokens (standard) / ~1048 (HD), which matches OpenAI's
      // published gpt-image-1 token counts for that size.
      const mp = (pxW * pxH) / 1_000_000
      const oi = quality === "HD" ? mp * 1000 : mp * 500 // imageOutput units
      return { textInput: ti, imageInput: ii, imageOutput: oi }
    },
    modelType: ModelType.IMAGE,
  },
  [GoogleImageGenerationModelType.NANO_BANANA_PRO]: {
    displayName: "Nano Banana Pro",
    unit: "quantity",
    ratesUSD: { imageOutput: 0.15 }, // $0.15 for 1K/2K, $0.30 for 4K (using lower rate)
    paramSchema: quantitySchema,
    meter: (params: QuantityParams): RateUsage => ({ imageOutput: params.quantity }),
    modelType: ModelType.IMAGE,
  },
  [OpenAIImageGenerationModelType.GPT_IMAGE_1_5]: {
    displayName: "GPT Image 1.5",
    unit: "quantity",
    ratesUSD: { imageOutput: 0.05 }, // medium/auto price (range: $0.013-$0.136)
    paramSchema: quantitySchema,
    meter: (params: QuantityParams): RateUsage => ({ imageOutput: params.quantity }),
    modelType: ModelType.IMAGE,
  },

  /**********
   * VIDEO MODELS
   *********/
  [GoogleVideoGenerationModelType.GEMINI_OMNI_FLASH_PREVIEW]: {
    displayName: "Gemini Omni Flash",
    unit: "seconds",
    ratesUSD: { secondsOutput: 0.125 },
    paramSchema: secondsSchema,
    meter: (params: SecondsParams): RateUsage => ({ secondsOutput: params.seconds }),
    modelType: ModelType.VIDEO,
  },
  [GoogleVideoGenerationModelType.GEMINI_OMNI_FLASH_1_1]: {
    displayName: "Gemini Omni Flash 1.1",
    unit: "seconds",
    ratesUSD: { secondsOutput: 0.03 },
    paramSchema: secondsSchema,
    meter: (params: SecondsParams): RateUsage => ({ secondsOutput: params.seconds }),
    modelType: ModelType.VIDEO,
  },
  [KlingAIVideoGenerationModelType.KLING_2_1]: {
    displayName: "Kling 2.1",
    unit: "seconds",
    ratesUSD: { secondsOutput: 0.07 }, // $0.35 per 5 seconds = $0.07/sec
    paramSchema: secondsSchema,
    meter: (params: SecondsParams): RateUsage => ({ secondsOutput: params.seconds }),
    modelType: ModelType.VIDEO,
  },

  [ReplicateVideoGenerationModelType.SEEDREAM_LITE]: {
    displayName: "SeeDream 1.0 Lite",
    unit: "seconds",
    ratesUSD: { secondsOutput: 0.016 }, // $0.08 per 5-second video
    paramSchema: secondsSchema,
    meter: (params: SecondsParams): RateUsage => ({ secondsOutput: params.seconds }),
    modelType: ModelType.VIDEO,
  },
  [ReplicateVideoGenerationModelType.SEEDREAM_PRO]: {
    displayName: "SeeDream 1.0 Pro",
    unit: "seconds",
    ratesUSD: { secondsOutput: 0.03 }, // $0.15 per 5-second video
    paramSchema: secondsSchema,
    meter: (params: SecondsParams): RateUsage => ({ secondsOutput: params.seconds }),
    modelType: ModelType.VIDEO,
  },

  // Wan 2.5 models (using 720p default rate for simplified metering)
  [WavespeedVideoGenerationModelType.WAN_2_5_T2V]: {
    displayName: "Wan 2.5 Text-to-Video",
    unit: "seconds",
    ratesUSD: { secondsOutput: 0.1 }, // $0.10/sec at 720p (480p: $0.05, 1080p: $0.15)
    paramSchema: secondsSchema,
    meter: (params: SecondsParams): RateUsage => ({ secondsOutput: params.seconds }),
    modelType: ModelType.VIDEO,
  },
  [WavespeedVideoGenerationModelType.WAN_2_5_I2V]: {
    displayName: "Wan 2.5 Image-to-Video",
    unit: "seconds",
    ratesUSD: { secondsOutput: 0.1 }, // $0.10/sec at 720p (480p: $0.05, 1080p: $0.15)
    paramSchema: secondsSchema,
    meter: (params: SecondsParams): RateUsage => ({ secondsOutput: params.seconds }),
    modelType: ModelType.VIDEO,
  },
  [WavespeedVideoGenerationModelType.WAN_2_5_T2V_FAST]: {
    displayName: "Wan 2.5 Text-to-Video Fast",
    unit: "seconds",
    ratesUSD: { secondsOutput: 0.068 }, // $0.068/sec at 720p (1080p: $0.102)
    paramSchema: secondsSchema,
    meter: (params: SecondsParams): RateUsage => ({ secondsOutput: params.seconds }),
    modelType: ModelType.VIDEO,
  },
  [WavespeedVideoGenerationModelType.WAN_2_5_I2V_FAST]: {
    displayName: "Wan 2.5 Image-to-Video Fast",
    unit: "seconds",
    ratesUSD: { secondsOutput: 0.068 }, // $0.068/sec at 720p (1080p: $0.102)
    paramSchema: secondsSchema,
    meter: (params: SecondsParams): RateUsage => ({ secondsOutput: params.seconds }),
    modelType: ModelType.VIDEO,
  },

  /**********
   * TTS MODELS
   *********/

  /**********
   * MUSIC (Suno) + AVATAR / LIP-SYNC
   *
   * No longer literal here either. Both are derived views over MUSIC_RATES / LIPSYNC_RATES in
   * `modalityPricing.ts`, the same tables that feed `musicModelPricing` / `lipsyncModelPricing`
   * and the unified resolver. Ten entries whose rates existed in two files now exist in one.
   *********/
  ...(musicEngineMeta as Record<string, EngineMeta>),
  ...(lipsyncEngineMeta as Record<string, EngineMeta>),

  /**********
   * GENERIC MODELS MODELS (Assume LLM params)
   *********/
  // The LLM/TTS keys arrive via the spreads above, which TypeScript sees as `Record<string, …>`
  // and so cannot prove covers every `ListedLLMModelType`. The derived views are generated from
  // the same enums, and `pricing.test.ts` asserts every listed model resolves — that coverage
  // check is what makes this cast safe, and it fails loudly if a model ever goes missing.
} as unknown as ModelMetadataMap

/**
 * Per-second output rate for a model at a specific resolution.
 *
 * This is the ONLY sanctioned way to price a per-second generation whose rate varies by tier.
 * Reading `ratesUSD.secondsOutput` directly gives the model's base (lowest-tier) rate, which is
 * wrong for any caller that submitted a higher tier — the bug this exists to prevent.
 *
 * Falls back to the base rate when the model has no per-resolution breakdown, so callers can use
 * it unconditionally. Returns null when the model isn't priced at all (e.g. HeyGen, which bills
 * in plan credits rather than output seconds — see VM-1013).
 */
export const getRatePerSecAtResolution = (
  modelId: keyof typeof MODEL_METADATA | string,
  resolution?: "480p" | "720p" | "1080p",
): number | null => {
  const meta = (MODEL_METADATA as Record<string, EngineMeta | undefined>)[modelId]
  if (!meta) return null

  const base = meta.ratesUSD.secondsOutput ?? null
  if (!resolution || !meta.resolutionRatesUSD) return base

  // The model IS tiered and a specific tier was asked for. If that tier isn't configured, this is
  // a misconfiguration — return null rather than quietly handing back the base (lowest) rate.
  // Silently falling back is precisely the failure this function exists to prevent: it is how
  // Aurora billed its 480p rate for 720p renders. Callers surface the null; a guard test asserts
  // every selectable model resolves.
  return meta.resolutionRatesUSD[resolution] ?? null
}
